import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

/**
 * Custom CSS-grid heatmap. Avoids d3 dep — color scale is a single
 * linear interpolation between two CSS-token-driven anchors.
 *
 * Used by: Demand Pulse (location × hour-of-day), Location Heatmap
 * (location × day-of-week), App Activity, Sales dashboard (day × hour-of-day leads).
 */

export interface HeatmapCell {
  rowKey: string;
  colKey: string;
  value: number;
}

export interface HeatmapMatrixProps {
  rows: string[];
  cols: string[];
  cells: HeatmapCell[];
  /** Format the cell tooltip / aria-label value. */
  formatValue?: (n: number) => string;
  /** Min/max anchors. Default = derived from cells. */
  minValue?: number;
  maxValue?: number;
  /** "low" anchor color. Default = `--bg-elevated`. */
  lowColor?: string;
  /** "high" anchor color. Default = `--accent-500`. */
  highColor?: string;
  emptyMessage?: string;
  className?: string;
  /** Px height of each cell. Default 28. */
  cellHeight?: number;
  /**
   * What an ABSENT cell means. Default `'zero'` preserves the original behaviour for every
   * existing caller.
   *
   * `'unknown'` exists because omitting a cell was the only way a caller could say "not measured",
   * and it did not work: the cell fell through to `?? 0`, drew in the low colour and reported `0`
   * in its tooltip — identical to a real zero. A caller suppressing thin buckets (say, conversion
   * where fewer than 30 sessions makes the ratio meaningless) was therefore publishing the exact
   * false statement it was suppressing them to avoid.
   */
  treatMissingAs?: 'zero' | 'unknown';
  /** Accessible name of the grid. Default "Heatmap". */
  ariaLabel?: string;
  /**
   * A cell's full description, used as its accessible name (and its native tooltip when there is
   * no `renderTooltip`). Default `row, col: value`. Put everything the tooltip shows in here too:
   * a keyboard or screen-reader user never sees the tooltip.
   */
  describeCell?: (row: string, col: string, value: number) => string;
  /**
   * A floating panel for the active cell — hovered, focused, or tapped on touch. Omit for the
   * native `title` tooltip.
   */
  renderTooltip?: (row: string, col: string, value: number) => React.ReactNode;
  /** Labels at the two ends of the legend. Default Low / High. */
  legendLabels?: { low: string; high: string };
}

interface ActiveCell {
  row: number;
  col: number;
  /** Viewport position of the cell's top-centre and bottom edge, for the fixed tooltip. */
  x: number;
  top: number;
  bottom: number;
}

const fmtNum = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

/**
 * Linear color interpolation expressed as CSS `color-mix`. Browsers
 * with color-mix support (90%+ today) render the gradient natively.
 * Fallback: solid lowColor.
 */
function cellColor(t: number, low: string, high: string): string {
  const pct = Math.max(0, Math.min(1, t));
  return `color-mix(in oklab, ${low} ${(1 - pct) * 100}%, ${high} ${pct * 100}%)`;
}

export function HeatmapMatrix({
  rows,
  cols,
  cells,
  formatValue = fmtNum,
  minValue,
  maxValue,
  lowColor = 'var(--bg-elevated)',
  highColor = 'var(--accent-500)',
  emptyMessage = 'No data',
  className,
  cellHeight = 28,
  treatMissingAs = 'zero',
  ariaLabel = 'Heatmap',
  describeCell,
  renderTooltip,
  legendLabels = { low: 'Low', high: 'High' },
}: HeatmapMatrixProps) {
  // EVERY HOOK BEFORE THE EARLY RETURN. `useMemo` used to sit after it, so a caller whose cells
  // went from non-empty to EMPTY between renders — switching a heatmap from a sessions metric to a
  // conversion metric that suppresses every thin bucket, say — rendered fewer hooks on the second
  // pass and React threw. The empty state is the crash path, which is the one a low-volume tenant
  // meets first.
  const cellMap = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) m.set(`${c.rowKey}::${c.colKey}`, c.value);
    return m;
  }, [cells]);
  const [focusCell, setFocusCell] = React.useState<[number, number]>([0, 0]);
  // CLAMPED TO THE CURRENT SHAPE. `rows` and `cols` are derived from query results on the pages
  // that already use this component, so they shrink when a period or a ranking changes. If the
  // stored index then pointed at a row or column that no longer exists, NO cell satisfied the
  // tabIndex condition, every cell sat at -1, and the grid became unreachable by Tab until it
  // remounted — a keyboard trap introduced into pages that had nothing to do with this change.
  const focusRow = Math.min(focusCell[0], Math.max(rows.length - 1, 0));
  const focusCol = Math.min(focusCell[1], Math.max(cols.length - 1, 0));
  // The cell whose tooltip is open. Stored with its position, taken from the element at the moment
  // it became active, so the tooltip needs no layout effect. Same shrinking-list hazard as focus:
  // an index past the current shape shows nothing rather than a neighbour's value.
  const [active, setActive] = React.useState<ActiveCell | null>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);
  const activate = (row: number, col: number, el: HTMLElement) => {
    if (!renderTooltip) return;
    const r = el.getBoundingClientRect();
    setActive({ row, col, x: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  // The position was taken when the tooltip opened, so anything that moves the cell closes it:
  // a scroll (capture, so a scrolling ancestor counts too) or a resize. And a tap elsewhere closes
  // it -- iOS does not blur a cell when you tap plain content, so blur alone would leave it open.
  const isOpen = active !== null;
  React.useEffect(() => {
    if (!isOpen) return;
    const close = () => setActive(null);
    const closeOutside = (e: PointerEvent) => {
      if (!gridRef.current?.contains(e.target as Node)) setActive(null);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [isOpen]);

  if (cells.length === 0 || rows.length === 0 || cols.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ minHeight: 240 }}
      >
        {emptyMessage}
      </div>
    );
  }

  const values = cells.map((c) => c.value).filter((v) => Number.isFinite(v));
  const min = minValue ?? Math.min(...values, 0);
  const max = maxValue ?? Math.max(...values, 1);
  const span = Math.max(1e-9, max - min);

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      <div
        role="grid"
        ref={gridRef}
        aria-label={ariaLabel}
        // min-w-max: the grid's box spans all its columns even when a narrow parent scrolls it, so
        // the sticky row names below have the whole width to stick within.
        className="grid min-w-max"
        onMouseLeave={() => setActive(null)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActive(null);
        }}
        style={{
          gridTemplateColumns: `120px repeat(${cols.length}, minmax(28px, 1fr))`,
          rowGap: 2,
          columnGap: 2,
        }}
      >
        <div />
        {cols.map((col) => (
          <div
            key={`h-${col}`}
            className="text-caption text-text-muted text-center pb-1 truncate"
            title={col}
          >
            {col}
          </div>
        ))}
        {rows.map((row, ri) => (
          <React.Fragment key={`r-${row}`}>
            {/* Sticky, so a grid wider than a phone keeps its row names while it scrolls sideways. */}
            <div
              className="sticky left-0 z-10 flex items-center truncate bg-bg-surface pr-2 text-caption text-text-secondary"
              title={row}
            >
              {row}
            </div>
            {cols.map((col, ci) => {
              const raw = cellMap.get(`${row}::${col}`);
              const missing = raw === undefined && treatMissingAs === 'unknown';
              const val = raw ?? 0;
              const t = (val - min) / span;
              const description = missing
                ? `${row}, ${col}: not enough data`
                : describeCell
                  ? describeCell(row, col, val)
                  : `${row}, ${col}: ${formatValue(val)}`;
              return (
                <div
                  key={`${row}::${col}`}
                  className={cn(
                    'rounded-sm transition-transform duration-150 hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
                    missing && 'border border-dashed border-border',
                  )}
                  style={{
                    height: cellHeight,
                    // No fill: visibly "nothing measured here", and distinguishable from the low
                    // end of the scale, which is a real zero. The outline is a TOKEN-BACKED border
                    // class, not a raw inset shadow — the design contract for this package forbids
                    // raw shadow primitives because they do not track the theme.
                    backgroundColor: missing ? 'transparent' : cellColor(t, lowColor, highColor),
                  }}
                  title={
                    renderTooltip
                      ? undefined
                      : missing
                        ? `${row} · ${col}: not enough data`
                        : describeCell
                          ? description
                          : `${row} · ${col}: ${formatValue(val)}`
                  }
                  role="gridcell"
                  aria-label={description}
                  // ROVING TABINDEX. Every cell was tabbable, so a 7x24 grid put 168 stops between
                  // the chart and the controls under it. One stop enters the grid; arrows move
                  // within it. The aria-label carries the missing state too — the tooltip said
                  // "not enough data" while the accessible name still read "0%", which reproduced
                  // the exact false measurement `treatMissingAs` exists to prevent, for the users
                  // least able to notice.
                  tabIndex={ri === focusRow && ci === focusCol ? 0 : -1}
                  onFocus={(e) => {
                    setFocusCell([ri, ci]);
                    activate(ri, ci, e.currentTarget);
                  }}
                  onMouseEnter={(e) => activate(ri, ci, e.currentTarget)}
                  // A tap on a touch screen: there is no hover, so the tap opens the tooltip. Not a
                  // toggle: a mouse click and a tap both arrive after the pointer has already
                  // entered (and focused) the cell, so a toggle would close what they just opened.
                  // A tap elsewhere closes it (the pointerdown listener above).
                  onClick={(e) => activate(ri, ci, e.currentTarget)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setActive(null);
                      return;
                    }
                    const moves: Record<string, [number, number]> = {
                      ArrowRight: [0, 1], ArrowLeft: [0, -1], ArrowDown: [1, 0], ArrowUp: [-1, 0],
                    };
                    const mv = moves[e.key];
                    if (!mv) return;
                    e.preventDefault();
                    const nr = Math.min(Math.max(ri + mv[0], 0), rows.length - 1);
                    const nc = Math.min(Math.max(ci + mv[1], 0), cols.length - 1);
                    setFocusCell([nr, nc]);
                    const next = e.currentTarget.parentElement?.parentElement?.querySelector<HTMLElement>(
                      `[data-cell="${nr}:${nc}"]`,
                    );
                    next?.focus();
                  }}
                  data-cell={`${ri}:${ci}`}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2 text-caption text-text-muted">
        <span>{legendLabels.low}</span>
        <div
          className="h-1.5 w-24 rounded-full"
          style={{ background: `linear-gradient(to right, ${lowColor}, ${highColor})` }}
        />
        <span>{legendLabels.high}</span>
      </div>
      {renderTooltip && active && active.row < rows.length && active.col < cols.length
        ? createPortal(
            <div
              // Visual only: the cell's accessible name already carries everything in it.
              aria-hidden="true"
              data-heatmap-tooltip=""
              className="pointer-events-none fixed z-50 max-w-xs rounded-md bg-text-primary px-2.5 py-1.5 text-caption text-bg-app shadow-md"
              style={{
                // Centred on the cell, but kept clear of the window's edges (the first and last
                // hours sit at the edges of a phone screen): half of max-w-xs, 20rem.
                left: Math.min(Math.max(active.x, 160), window.innerWidth - 160),
                // Above the cell, or below it when the cell is too near the top of the window for a
                // tall breakdown (eight sources is about 180 px).
                top: active.top > 200 ? active.top - 6 : active.bottom + 6,
                transform: active.top > 200 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
              }}
            >
              {renderTooltip(
                rows[active.row]!,
                cols[active.col]!,
                cellMap.get(`${rows[active.row]}::${cols[active.col]}`) ?? 0,
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
